import { RedoOutlined, SearchOutlined } from "@ant-design/icons"
import { useQuery } from "@tanstack/react-query"
import { Button, Card, ConfigProvider, Form, Input, Select, Space } from "antd"
import type React from "react"
import { memo, useEffect } from "react"
import type { AdminSearchParams } from "@/services/admin/adminApi"
import { roleService } from "@/services/role/roleApi"

interface AdminSearchFormProps {
  onSearch: (values: Omit<AdminSearchParams, "page" | "per_page">) => void
  loading?: boolean
  initialValues?: Omit<AdminSearchParams, "page" | "per_page">
}

const STATUS_OPTIONS = [
  { value: 1, label: "Hoạt động" },
  { value: 0, label: "Khóa" },
]

/**
 * Component Form tìm kiếm tài khoản Admin
 */
const AdminSearchForm: React.FC<AdminSearchFormProps> = memo(({ onSearch, loading = false, initialValues }) => {
  const [form] = Form.useForm()

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues)
    }
  }, [initialValues, form])

  // Lấy danh sách các vai trò hoạt động để làm bộ lọc
  const { data: rolesResult } = useQuery({
    queryKey: ["roles_active_list_search"],
    queryFn: () => roleService.list(),
  })

  const roleOptions =
    rolesResult?.data?.map((item: any) => ({
      value: item.id,
      label: item.name,
    })) || []

  const handleSearch = () => {
    form.validateFields().then((values) => {
      onSearch(values)
    })
  }

  const handleReset = () => {
    form.resetFields()
    onSearch({})
  }

  return (
    <ConfigProvider
      theme={{
        components: {
          Form: {
            itemMarginBottom: 0,
          },
        },
      }}
    >
      <Card>
        <Form
          form={form}
          initialValues={{
            query: "",
            is_active: undefined,
            role_id: undefined,
          }}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
        >
          <Form.Item name="query" label="Tìm kiếm" colon={false} className="mb-0">
            <Input allowClear autoComplete="off" placeholder="Tìm theo tên, email, username, id" />
          </Form.Item>
          <Form.Item name="is_active" label="Trạng thái" colon={false} className="mb-0">
            <Select allowClear placeholder="Chọn trạng thái" options={STATUS_OPTIONS} />
          </Form.Item>

          <Form.Item name="role_id" label="Vai trò" colon={false} className="mb-0">
            <Select allowClear placeholder="Chọn vai trò" options={roleOptions} />
          </Form.Item>

          <Form.Item label=" " colon={false} className="mb-0">
            <Space>
              <Button type="default" icon={<RedoOutlined />} onClick={handleReset}>
                Đặt lại
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SearchOutlined />}
                onClick={handleSearch}
              >
                Tìm kiếm
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </ConfigProvider>
  )
})

AdminSearchForm.displayName = "AdminSearchForm"

export default AdminSearchForm
